import { Container, MenuItem, Rating, Select, SelectChangeEvent, Stack, Typography } from "@mui/material";
import { graphql, useStaticQuery } from "gatsby";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { ReviewsArray, ReviewsListProps } from "../types";
import CarouselClient from './CarouselClient'
import { ReviewCard } from "../card";
import { ReviewsListHeader } from "../list/header";

const sliderProps: any = {
    swipeable: true,
    draggable: true,
    showDots: false,
    ssr: true,
    shouldResetAutoplay: true,
    infinite: false,
    rewind: true,
    rewindWithAnimation: true,
    // partialVisbile: true,
    // centerMode: true,
    autoPlay: true,
    renderDotsOutside: true,
    arrows: true,
    autoPlaySpeed: 10000,
    // renderButtonGroupOutside: true,
    keyBoardControl: false,
    // customTransition: all .5,
    transitionDuration: 500,
    // containerClass: 'carousel-container',
    // removeArrowOnDeviceType: ["tablet", "mobile"],
    // deviceType: this.props.deviceType,
    // dotListClass: custom-dot-list-style,
    // itemClass: 'carousel-item-padding-40-px',
    responsive: {
        largeDesktop: {
            breakpoint: { max: 6000, min: 2000 },
            items: 4,
            partialVisibilityGutter: 40,
            slidesToSlide: 1 // optional, default to 1.
        },
        desktop: {
            breakpoint: { max: 2000, min: 1024 },
            items: 3,
            partialVisibilityGutter: 40,
            slidesToSlide: 1 // optional, default to 1.
        },
        tablet: {
            breakpoint: { max: 1024, min: 464 },
            items: 3,
            partialVisibilityGutter: 30,
            slidesToSlide: 1 // optional, default to 1.
        },
        mobile: {
            breakpoint: { max: 464, min: 0 },
            items: 1,
            partialVisibilityGutter: 30,
            slidesToSlide: 1 // optional, default to 1.
        }
    }
}

const compactSliderProps: any = {
    swipeable: true,
    draggable: true,
    showDots: true,
    ssr: true,
    shouldResetAutoplay: true,
    infinite: true,
    // partialVisbile: true,
    // centerMode: true,
    removeArrowOnDeviceType: ["desktop", "tablet", "mobile"],
    autoPlay: true,
    renderDotsOutside: false,
    arrows: true,
    autoPlaySpeed: 10000,
    // renderButtonGroupOutside: true,
    keyBoardControl: false,
    // customTransition: all .5,
    transitionDuration: 500,
    // containerClass: 'carousel-container',
    // removeArrowOnDeviceType: ["tablet", "mobile"],
    // deviceType: this.props.deviceType,
    // dotListClass: custom-dot-list-style,
    // itemClass: 'carousel-item-padding-40-px',
    responsive: {
        tablet: {
            breakpoint: { max: 100000, min: 464 },
            items: 1,
            partialVisibilityGutter: 30,
            slidesToSlide: 1 // optional, default to 1.
        },
        mobile: {
            breakpoint: { max: 464, min: 0 },
            items: 1,
            partialVisibilityGutter: 30,
            slidesToSlide: 1 // optional, default to 1.
        }
    }
}

export const ReviewsCarosel = ({ order, allowSort, featured, compact, list }: ReviewsListProps) => {
    // const elId = useId();
    const topReviewRef = useRef<HTMLDivElement>(null)

    const reviewsData = useStaticQuery<Queries.ReviewsListQuery>(graphql`
      query ReviewsList {
        reviewList: allCustomJudgeMeReviewsList {
          nodes {
            id
            rating
            body
            featured
            published
            title
            verified
            created_at
            reviewer {
                name
            }
          }
        }
        judgeMeStoreReviewsRating {
          all_reviews_rating
        }
      }
    `);

    const allReviewsNotFiltered = useMemo(() => {
        return [
            ...reviewsData.reviewList.nodes.filter(e => e.published && e.rating >= 3)
        ];
    }, []);

    const getAllReviews = () => {
        const d = [
            ...allReviewsNotFiltered
                .filter(e => e.published && (featured ? e.featured : true))
                .filter(e => e.rating >= 3)
        ];

        const orderState = ordering;

        if (orderState === 'recent') {
            d.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
        } else if (orderState === 'lowest') {
            d.sort((a, b) => a.rating - b.rating);
        } else if (orderState === 'highest') {
            d.sort((a, b) => b.rating - a.rating);
        } else {
            d.sort((a, b) => b.body.length - a.body.length);
        }
        return d.slice(0, 8);
    };

    const [ordering, setOrdering] = useState<ReviewsListProps['order']>(order || 'helpful');
    const [allReviews, setAllReviews] = useState<ReviewsArray>(getAllReviews());

    const scrollTopReview = () => {
        if (topReviewRef.current) {
            topReviewRef.current.scrollIntoView({ behavior: "smooth" });
        }
    }


    const handleChange = (event: SelectChangeEvent) => {
        setOrdering(event.target.value as ReviewsListProps['order']);
        scrollTopReview()
    };

    useEffect(() => {
        setAllReviews(getAllReviews());
    }, [ordering, featured]);

    const sorter = () => (
        <Select variant="standard" aria-label="Sort reviews by" value={ordering} onChange={handleChange}>
            <MenuItem value={'helpful'}>Most helpful</MenuItem>
            <MenuItem value={'recent'}>Recent</MenuItem>
            <MenuItem value={'highest'}>Highest rated</MenuItem>
            <MenuItem value={'lowest'}>Lowest rated</MenuItem>
        </Select>
    );

    return (
        <Container maxWidth="xl" sx={{ mt: 3 }} disableGutters>
            <Stack gap={4}>
                <Stack gap={1} justifyContent="center" alignItems="center">
                    {!list ? (
                        <ReviewsListHeader rating={reviewsData.judgeMeStoreReviewsRating.all_reviews_rating} count={allReviewsNotFiltered.length} list={list} compact={compact} />
                    ) : (
                        <Typography variant={compact || list ? 'h3' : 'h1'}>Reviews</Typography>
                    )}
                    {!list && (
                        <Rating
                            size="large"
                            readOnly
                            precision={0.1}
                            value={reviewsData.judgeMeStoreReviewsRating.all_reviews_rating}
                        />
                    )}
                    {allowSort && !list && sorter()}
                </Stack>

                <div className="slider-container">
                    <CarouselClient {...(compact ? compactSliderProps : sliderProps)}>
                        {allReviews.map(e => (
                            <ReviewCard key={e.id} {...e} cardProps={{ sx: { marginLeft: '10px', marginRight: '10px' } }} />
                        ))}
                    </CarouselClient>
                </div>
            </Stack>
        </Container>
    );
};
